// `excel_read_sync_anchors` (M3 row f): pure parsing of the `_pcp_sync` manifest a platform-generated
// workbook carries, and the shaping of its anchors into the simulator's output
// (`evals/agent_v2/simulator/executor.py::excel_read_sync_anchors`). No Office.js here: the executor
// reads the cells, this module turns them into JSON.
//
// The manifest is written by `peerception-api/api/worksheets/sync/workbook_scaffolding.py`: a veryHidden
// sheet `_pcp_sync`, compact snake_case JSON chunked in column A (30 000 chars per cell, row order).
// Port of the modelling add-in's `services/excel/manifest.ts` (never imported across repos).
import { colToLetter } from './a1';
import type { SyncAnchor, SyncAnchorsOutput } from '../../types/excel';

export const PCP_SYNC_SHEET = '_pcp_sync';
/** Rows of column A read for the chunks (40 × 30 000 chars = 1.2 MB, far beyond any real manifest). */
export const MANIFEST_MAX_CHUNK_ROWS = 40;
/** Cap on the objects reported for a one-row-per-object sheet (the simulator's cap). */
export const ANCHOR_MAX_OBJECTS = 400;

export type AnchorAxis = 'rows' | 'cols';

/** One `anchor_positions` entry of the manifest, validated: the sheet and the 1-based row (`cols`
 *  axis: one column per object, ids on that row) or column (`rows` axis: one row per object, ids in
 *  that column). */
export interface AnchorLine {
  sheet: string;
  axis: AnchorAxis;
  /** 1-based row (`cols`) or column (`rows`) index. */
  index: number;
}

export interface SyncManifest {
  anchor_positions: Record<string, { axis?: unknown; row?: unknown; col?: unknown }>;
  sheet_footprints: Record<string, unknown> | null;
  entity_type: string | null;
  entity_id: number | null;
  scenario_id: number | null;
}

export class InvalidManifestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidManifest';
  }
}

/** Concatenate the column-A chunks (a 2-D `values` grid of `A1:A<n>`), in row order; non-strings are skipped. */
export function manifestTextFromColumn(values: unknown[][]): string {
  return values.map((row) => (typeof row?.[0] === 'string' ? (row[0] as string) : '')).join('');
}

/** Parse the manifest JSON; throws `InvalidManifestError` when it is not a JSON object. */
export function parseManifestText(text: string): SyncManifest {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new InvalidManifestError('The _pcp_sync manifest is not valid JSON');
  }
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) throw new InvalidManifestError('The _pcp_sync manifest is not a JSON object');
  const m = raw as Record<string, unknown>;
  const positions = m.anchor_positions;
  const footprints = m.sheet_footprints;
  return {
    anchor_positions: typeof positions === 'object' && positions !== null && !Array.isArray(positions) ? (positions as SyncManifest['anchor_positions']) : {},
    sheet_footprints: typeof footprints === 'object' && footprints !== null && !Array.isArray(footprints) ? (footprints as Record<string, unknown>) : null,
    entity_type: typeof m.entity_type === 'string' && m.entity_type ? m.entity_type : null,
    entity_id: Number.isInteger(m.entity_id) ? (m.entity_id as number) : null,
    scenario_id: Number.isInteger(m.scenario_id) ? (m.scenario_id as number) : null,
  };
}

/** The valid anchor lines of a manifest (entries with an unknown axis or a missing index are skipped). */
export function anchorLines(manifest: SyncManifest): AnchorLine[] {
  const out: AnchorLine[] = [];
  for (const [sheet, pos] of Object.entries(manifest.anchor_positions)) {
    if (typeof pos !== 'object' || pos === null) continue;
    if (pos.axis === 'cols' && Number.isInteger(pos.row) && (pos.row as number) >= 1) out.push({ sheet, axis: 'cols', index: pos.row as number });
    else if (pos.axis === 'rows' && Number.isInteger(pos.col) && (pos.col as number) >= 1) out.push({ sheet, axis: 'rows', index: pos.col as number });
  }
  return out;
}

const isEmpty = (v: unknown): boolean => v === '' || v === null || v === undefined;

/**
 * Objects along one anchor line. `cells` are the values read along the line (a row for the `cols`
 * axis, a column for the `rows` axis) and `first` is the 1-based index of `cells[0]` on that line
 * (column for `cols`, row for `rows`). Keys: column letters or row numbers; empty cells are skipped;
 * the `rows` axis is capped at ANCHOR_MAX_OBJECTS objects.
 */
export function anchorObjects(line: AnchorLine, cells: unknown[], first: number): SyncAnchor {
  const objects: Record<string, unknown> = {};
  let n = 0;
  for (let i = 0; i < cells.length; i++) {
    const v = cells[i];
    if (isEmpty(v)) continue;
    if (line.axis === 'rows' && n >= ANCHOR_MAX_OBJECTS) break;
    objects[line.axis === 'cols' ? colToLetter(first + i) : String(first + i)] = v;
    n++;
  }
  return line.axis === 'cols' ? { axis: 'cols', row: line.index, objects } : { axis: 'rows', col: line.index, objects };
}

/** The tool result for a present manifest (simulator shape): anchors, footprints and the entity when known. */
export function anchorsOf(manifest: SyncManifest, anchors: Record<string, SyncAnchor>): SyncAnchorsOutput {
  const out: SyncAnchorsOutput = { present: true, anchors, footprints: manifest.sheet_footprints };
  if (manifest.entity_type && manifest.entity_id !== null) out.entity = { type: manifest.entity_type, id: manifest.entity_id, scenario_id: manifest.scenario_id };
  return out;
}
